import React, { useState, useRef, useEffect } from "react";
import Label from "../Label";
import { DownArrowIcon, SearchIcons } from "../Icons";

const SearchableCodeInput = ({
  options = [],
  selectedOption,
  onChange,
  placeholder,
  label,
  error,
  codeKey = "code",
  titleKey = "title",
}) => {
  const [searchQuery, setSearchQuery] = useState("");
  const [isOpen, setIsOpen] = useState(false);
  const searchInputRef = useRef(null);
  const dropdownRef = useRef(null);

  const handleSelect = (option) => {
    onChange(option);
    setIsOpen(false);
    setSearchQuery("");
  };

  const filteredOptions = options
    ? options.filter((item) => {
        const title = item[titleKey]
          ? item[titleKey].toString().toLowerCase()
          : "";
        const query = searchQuery.toLowerCase();
        return title.includes(query);
      })
    : [];

  const handleKeyDown = (e, item) => {
    switch (e.key) {
      case "Enter":
        e.preventDefault();
        handleSelect(item);
        break;
      case "ArrowDown":
        e.preventDefault();
        const nextElement = e.target.nextElementSibling;
        if (nextElement) nextElement.focus();
        break;
      case "ArrowUp":
        e.preventDefault();
        const prevElement = e.target.previousElementSibling;
        if (prevElement) prevElement.focus();
        break;
      default:
        break;
    }
  };

  const handleSearchKeyDown = (e) => {
    if (e.key === "ArrowDown" && e.target === searchInputRef.current) {
      const firstElement =
        dropdownRef.current.querySelector("li[tabindex='0']");
      if (firstElement) firstElement.focus();
    }
  };

  const handleClickOutside = (event) => {
    if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
      setIsOpen(false);
    }
  };

  useEffect(() => {
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  return (
    <div className="relative" ref={dropdownRef}>
      <div
        tabIndex={0}
        onKeyDown={(e) => e.key === "Enter" && setIsOpen(!isOpen)}
        onClick={() => setIsOpen(!isOpen)}
        className={`w-full cursor-pointer shadow-sm rounded-xl flex justify-between items-center py-2.5 px-3 select-input-wrapper
          ${error ? "select-input-error" : "select-input-default"}`}
      >
        {selectedOption ? (
          <div className="space-y-1 w-full overflow-hidden">
            <Label
              htmlFor={label}
              className="block text-xs font-medium select-input-lable"
              text={label}
            />
            <p className="text-sm font-medium text-text-primary truncate">
              {selectedOption[codeKey]} - {selectedOption[titleKey]}
            </p>
          </div>
        ) : (
          <div className="flex justify-between w-full items-center">
            <div className="space-y-1">
              <p className={"block text-xs font-medium select-input-lable"}>
                {label}
              </p>
              <p className="select-input-placeholder text-xs">{placeholder}</p>
            </div>
            <DownArrowIcon className="w-4 h-4 mr-2" />
          </div>
        )}
      </div>

      <div
        className={`absolute z-10 w-full select-input-dropdow-wrapper
        ${
          isOpen
            ? "opacity-100 scale-100 pointer-events-auto"
            : "opacity-0 scale-95 pointer-events-none"
        }
        `}
        style={{ left: 0 }}
      >
        {isOpen && (
          <ul className="absolute flex z-10 flex-col space-y-4 my-1 w-full max-h-[260px] hide-scrollbar overflow-scroll h-auto select-input-dropdown">
            <li>
              <form
                onSubmit={(e) => {
                  e.preventDefault();
                  if (filteredOptions.length > 0) {
                    handleSelect(filteredOptions[0]);
                  }
                }}
              >
                <div className="max-w-full px-3 py-1 rounded-full flex justify-start items-center search-input-wrapper">
                  <SearchIcons className="w-6 h-6 relative" />
                  <input
                    ref={searchInputRef}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    value={searchQuery}
                    placeholder="Search..."
                    autoFocus={true}
                    onKeyDown={handleSearchKeyDown}
                    className="w-full select-input-search-input"
                  />
                </div>
              </form>
            </li>

            {filteredOptions.length > 0 ? (
              filteredOptions.map((item) => (
                <li
                  tabIndex={0}
                  key={item.id}
                  className={`${
                    selectedOption?.id === item.id &&
                    "select-input-item-selected"
                  } flex justify-between select-input-item`}
                  onClick={() => handleSelect(item)}
                  onKeyDown={(e) => handleKeyDown(e, item)}
                >
                  <div className="flex space-x-2 items-center w-full">
                    <span className="font-semibold min-w-[60px]">
                      {item[codeKey]}
                    </span>
                    <span className="text-gray-400">-</span>
                    <span className="truncate">{item[titleKey]}</span>
                  </div>
                </li>
              ))
            ) : (
              <li className="flex flex-col items-center py-6 justify-center text-center text-sm text-text-primary">
                <p>No results found</p>
              </li>
            )}
          </ul>
        )}
      </div>
    </div>
  );
};

export default SearchableCodeInput;
